import React from 'react';
import { Box, Grid, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import CodeBlock from './CodeBlock';

/** One labelled config field with a pass/fail glyph. */
const Field = ({ label, ok, okColor = 'success', failColor = 'error', text, children }) => (
  <Grid size={{ xs: 12, md: 6 }}>
    <Box
      sx={{
        p: 2,
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      {text !== undefined && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
          {ok ? (
            <CheckCircleOutlineIcon color={okColor} fontSize="small" />
          ) : (
            <CancelIcon color={failColor} fontSize="small" />
          )}
          <Typography variant="body2">{text}</Typography>
        </Stack>
      )}
      {children}
    </Box>
  </Grid>
);

/**
 * Read-only summary of the server's Jira environment configuration.
 * Purely presentational — the parent owns fetching and refresh.
 */
const JiraConfigStatus = ({ status }) => (
  <Grid container spacing={2}>
    <Field
      label="Base URL"
      ok={status.baseUrlSet}
      text={
        status.baseUrlSet
          ? status.baseUrlFormat === 'valid'
            ? 'Set (valid format)'
            : 'Set (invalid format)'
          : 'Not set'
      }
    />

    <Field label="API Email" ok={status.apiEmailSet} text={status.apiEmailSet ? 'Set' : 'Not set'} />

    <Field
      label="API Token"
      ok={status.apiTokenSet}
      text={status.apiTokenSet ? `Set (${status.apiTokenLength} characters)` : 'Not set'}
    />

    <Field
      label="JQL Query"
      ok={status.jqlQuerySet}
      failColor="warning"
      text={status.jqlQuerySet ? 'Set' : 'Using default'}
    >
      {status.jqlQuery && (
        <Box sx={{ mt: 1.5 }}>
          <CodeBlock fontSize="0.75rem" maxHeight={140}>
            {status.jqlQuery}
          </CodeBlock>
        </Box>
      )}
    </Field>

    <Field label="Tickets in Database">
      <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {status.ticketCountInDatabase || 0}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Total tickets synced
      </Typography>
    </Field>

    <Field label="Server Environment">
      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          Node {status.serverInfo?.nodeVersion || 'Unknown'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Platform {status.serverInfo?.platform || 'Unknown'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Environment {status.nodeEnv || 'Unknown'}
        </Typography>
      </Stack>
    </Field>
  </Grid>
);

export default JiraConfigStatus;
